import { Image, Text, View } from '@react-pdf/renderer';
import { Trans, useTranslation } from 'react-i18next';

import { PublicEventDetailsResponse } from '@hubs101/booking-api-skd-client/lib/types';
import { absoluteFilePath } from '@utils/fn';
import dayjs from 'dayjs';
import { styles } from '../../styles';
import { useMemo } from 'react';

interface ParticipantFeedbackProps {
  event: PublicEventDetailsResponse;
}

const ParticipantFeedback = ({ event }: ParticipantFeedbackProps) => {
  const { t } = useTranslation();

  const eventName = useMemo(
    () => `${event?.name} ${dayjs(event?.start_date).format('YYYY')}`,
    [event?.name, event?.start_date]
  );

  const testimonialsItems = useMemo(
    () =>
      event?.website_content?.testimonials
        ?.filter(e => e.is_partner)
        ?.sort((a, b) => a.order - b.order)
        ?.map((e, id) => ({
          id: String(id),
          name: [e?.first_name, e?.last_name]?.filter(e => e).join(' '),
          jobTitle: [e?.job_title, e?.company_name]?.filter(e => e).join(' - '),
          videoLink: e?.video_url,
          logoLink: absoluteFilePath(e?.company_logo),
          citation: e?.citation,
        })),
    [event?.website_content?.testimonials]
  );

  return (
    <View style={styles.bodyContainer}>
      <View
        style={{
          ...styles.leftSideBody,
          backgroundColor: event.design.themeColor,
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
      >
        <Text style={{ ...styles.whiteTitle, width: 330 }}>
          {t('participant-feedback')}
        </Text>
        <View style={styles.whiteBodyLineBar} />

        <Text style={styles.participantSubtitle}>
          <Trans i18nKey="participant-feedback-subtitle" values={{ eventName }}>
            What our participants say about
            <Text style={styles.participantEventSubtitle}>{eventName}</Text>.
          </Trans>
        </Text>
      </View>
      <View
        style={{
          ...styles.rightSideBody,
          gap: 20,
          flexDirection: 'row',
        }}
      >
        <View style={styles.participantFeedbackRightWrapper}>
          {testimonialsItems?.map(item => (
            <View style={styles.testimonialItem} key={item.id}>
              <View style={styles.testimonialItemHeader}>
                <View
                  style={{
                    transform: 'rotate(180deg)',
                  }}
                >
                  <Text
                    style={{
                      ...styles.participantEventSubtitle,
                      fontSize: 35,
                      position: 'relative',
                      top: 20,
                    }}
                  >
                    ”
                  </Text>
                </View>
                <Text style={styles.testimonialItemHeaderText}>
                  {item?.citation}
                </Text>
                <View>
                  <Text
                    style={{
                      ...styles.participantEventSubtitle,
                      fontSize: 35,
                    }}
                  >
                    „
                  </Text>
                </View>
              </View>
              <View style={styles.testimonialItemBody}>
                <Text style={{ ...styles.matchMakingTitle, fontSize: 10 }}>
                  {item.name}
                </Text>
                <Text style={{ ...styles.matchMakingBody, fontSize: 8 }}>
                  {item.jobTitle}
                </Text>
                <View style={styles.participantLogoWrapper}>
                  <Image style={styles.eventHeaderLogo} src={item.logoLink} />
                </View>
              </View>
            </View>
          ))}
        </View>
      </View>
    </View>
  );
};

export default ParticipantFeedback;
